import { useEffect, useId, useRef, useState } from 'react';
import { ApiError } from '../../identity/types';
import { ErrorSummary, type SummaryIssue } from '../../job-roles/components/ErrorSummary';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { healthcareErrorMessage } from '../healthcareErrors';
import { healthcareRatesGateway } from '../index';
import { HEALTHCARE_TERM_HINTS, HdcErrorCode, type Discipline } from '../types';
import '../../job-roles/job-roles.css';
import '../healthcare-rates.css';

export interface AddDisciplineDialogProps {
  /** What the person had typed in the picker, to start the name from. */
  initialName?: string | undefined;
  /** The industry tags a new worker category carries by default (the company's primary industry's sector). Used only to suggest it first. */
  defaultTags: string[];
  /** The company's primary industry in words, shown so the person can see what the category is suggested for. Null when none is chosen. */
  industryLabel: string | null;
  /** True for a healthcare company: the dialog says that a worker category is called a discipline there. */
  healthcare?: boolean | undefined;
  onClose: () => void;
  /** Called with the saved worker category. The caller selects it; this dialog only closes (focus returns to whatever opened it). */
  onAdded: (discipline: Discipline) => void;
}

type Field = 'name' | 'code';

const NAME_MAX = 100;
const CODE_MAX = 20;
const DESCRIPTION_MAX = 500;

/**
 * "Add a worker category": a company-own worker category (the worker's professional or trade classification, such as Registered Nurse or Electrician; called a
 * discipline in many healthcare organizations). It is SAVED for the company by the server, so it is offered for every later employee, and it is never another
 * company's. It sits on top of the form that opened it, which stays mounted underneath, so nothing already typed is lost. Duplicate names and short codes
 * (against EPN's defaults and the company's own, ignoring case and spacing) are shown on the field they are about; any other failure keeps this form and shows
 * the server's reference id when it has one. The server decides permission. A company admin does not pick industries here: the category is suggested for the
 * company's own industry.
 */
export function AddDisciplineDialog({ initialName = '', defaultTags, industryLabel, healthcare = false, onClose, onAdded }: AddDisciplineDialogProps) {
  const uid = useId();
  const formId = `${uid}-form`;
  const [name, setName] = useState(initialName.trim().slice(0, NAME_MAX));
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const codeRef = useRef<HTMLInputElement>(null);
  const [focusSummary, setFocusSummary] = useState(0);

  const issues: SummaryIssue[] = (['name', 'code'] as Field[]).filter((f) => errors[f]).map((f) => ({ key: f, message: errors[f]! }));

  // An error summary takes focus when it appears, so a keyboard or screen-reader user hears what to fix.
  useEffect(() => {
    if (focusSummary > 0) summaryRef.current?.focus();
  }, [focusSummary]);

  const clearError = (key: Field) => setErrors((prev) => { const next = { ...prev }; delete next[key]; return next; });
  const focusField = (key: string) => (key === 'code' ? codeRef : nameRef).current?.focus();

  async function submit() {
    const cleanName = name.replace(/\s+/g, ' ').trim();
    const cleanCode = code.trim().toUpperCase();
    const found: Partial<Record<Field, string>> = {};
    if (!cleanName) found.name = 'Enter a worker category name.';
    else if (cleanName.length > NAME_MAX) found.name = `The name must be ${NAME_MAX} characters or fewer.`;
    if (!cleanCode) found.code = 'Enter a short code, like RN or ELEC.';
    else if (cleanCode.length > CODE_MAX) found.code = `The short code must be ${CODE_MAX} characters or fewer.`;
    setErrors(found);
    setFormError(null);
    if (Object.keys(found).length > 0) {
      setFocusSummary((n) => n + 1);
      return;
    }
    setBusy(true);
    try {
      const created = await healthcareRatesGateway.createDiscipline({ name: cleanName, code: cleanCode, description: description.trim(), industries: defaultTags, isActive });
      onAdded(created);
    } catch (err) {
      setBusy(false);
      if (err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.DisciplineNameExists) {
        setErrors({ name: 'A worker category with this name already exists. Choose a different name, or pick the existing worker category.' });
        setFocusSummary((n) => n + 1);
      } else if (err instanceof ApiError && err.status === 409 && err.code === HdcErrorCode.DisciplineCodeExists) {
        setErrors({ code: 'This short code is already used by another worker category. Choose a different code.' });
        setFocusSummary((n) => n + 1);
      } else {
        // The form and everything typed stay. A server failure includes its reference id (friendlyErrorMessage).
        setFormError(healthcareErrorMessage(err, 'The worker category could not be added.'));
      }
    }
  }

  const describe = (key: Field, hintId?: string) => [errors[key] ? `${uid}-${key}-error` : null, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <ModalDialog
      title="Add a worker category"
      size="md"
      busy={busy}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="jr-btn jr-btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" form={formId} className="jr-btn jr-btn--primary" disabled={busy}>{busy ? 'Adding…' : 'Add worker category'}</button>
        </>
      )}
    >
      <form id={formId} noValidate onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <p className="jr-hint hdc-intro">
          A worker category describes the kind of worker, like Registered Nurse, Electrician or Server. It is not the work performed, and it does not set pay or overtime status.
          It is saved for your company so you can choose it again. Everything you have entered so far is kept.
          {healthcare ? ` ${HEALTHCARE_TERM_HINTS.workerCategory}` : ''}
        </p>
        <ErrorSummary ref={summaryRef} issues={issues} onSelect={focusField} />
        <div className="jr-grid">
          <div className="jr-field">
            <label htmlFor={`${uid}-name`}>Name <span aria-hidden="true" className="jr-req">*</span></label>
            <input
              ref={nameRef} id={`${uid}-name`} data-autofocus value={name} maxLength={NAME_MAX} autoComplete="off" aria-required="true"
              aria-invalid={errors.name ? true : undefined} aria-describedby={describe('name')}
              onChange={(e) => { setName(e.target.value); clearError('name'); }}
            />
            {errors.name && <p id={`${uid}-name-error`} className="jr-error" role="alert">{errors.name}</p>}
          </div>
          <div className="jr-field">
            <label htmlFor={`${uid}-code`}>Short code <span aria-hidden="true" className="jr-req">*</span></label>
            <input
              ref={codeRef} id={`${uid}-code`} value={code} maxLength={CODE_MAX} autoComplete="off" aria-required="true" className="hdc-upper"
              aria-invalid={errors.code ? true : undefined} aria-describedby={describe('code', `${uid}-code-hint`)}
              onChange={(e) => { setCode(e.target.value.toUpperCase()); clearError('code'); }}
            />
            <p id={`${uid}-code-hint`} className="jr-hint">Up to {CODE_MAX} characters. It is shown in upper case.</p>
            {errors.code && <p id={`${uid}-code-error`} className="jr-error" role="alert">{errors.code}</p>}
          </div>
          <div className="jr-field jr-grid__wide">
            <label htmlFor={`${uid}-description`}>Description (optional)</label>
            <textarea id={`${uid}-description`} rows={2} value={description} maxLength={DESCRIPTION_MAX} aria-describedby={`${uid}-description-hint`} onChange={(e) => setDescription(e.target.value)} />
            <p id={`${uid}-description-hint`} className="jr-hint">{description.length} of {DESCRIPTION_MAX} characters.</p>
          </div>
          <div className="jr-field">
            <span className="jr-fieldhead__label">Suggested for</span>
            <p data-testid="worker-category-industry">{industryLabel ?? 'Your company (no industry chosen)'}</p>
            <p className="jr-hint">Only used to list it first for your industry. You can choose it for any employee.</p>
          </div>
          <div className="jr-field">
            <span className="jr-fieldhead__label">Status</span>
            <label className="hdc-check">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span>Active (offered when choosing a worker category)</span>
            </label>
          </div>
        </div>
        {formError && <p className="jr-error jr-error--block" role="alert" data-testid="discipline-form-error">{formError}</p>}
      </form>
    </ModalDialog>
  );
}
